<template>
    <b-container>
        <div class="row text-dark py-4">Create new users, customize user permissions, and remove users from your account.</div>

        <!-- Users Stats -->
        <div class="row border-1 text-center text-dark px-2 py-4">
            <!-- Total Users -->
            <div class="col-lg-4 col-md-6 col-12 my-2">
                <div class="flex flex-col items-center">
                    <div class="flex items-center">
                        <div class="font-semibold text-uppercase">Total Users</div>
                        <div class="pl-3">
                            <i class="fa fa-info-circle text-grey"
                               data-toggle="tooltip"
                               data-placement="top"
                               title="This is the total number of users that have accepted their invite and have not been deactivated.">
                            </i>
                        </div>
                    </div>
                    <div v-if="userStatsLoader" class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                    <div v-else class="font-size-21 font-weight-light">{{ userStats.totalUsers }}</div>
                    <div class="no-btn-properties">
                    </div>
                </div>
            </div>

            <!-- Deactivated Users -->
            <div class="col-lg-4 col-md-6 col-12 my-2">
                <div class="flex flex-col items-center">
                    <div class="flex items-center">
                        <div class="font-semibold text-uppercase">Deactivated Users</div>
                        <div class="pl-3">
                            <i class="fa fa-info-circle text-grey"
                               data-toggle="tooltip"
                               data-placement="top"
                               title="Users who no longer have access to the account, but their profile stays in the
                                      account to preserve historical activities.">
                            </i>
                        </div>
                    </div>
                    <div v-if="userStatsLoader" class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                    <div v-else class="font-size-21 font-weight-light">{{ userStats.deactivatedUsers }}</div>
                    <div class="no-btn-properties">
                        <b-button @click.prevent="reviewDeactivatedUsers" class="no-btn-properties">Review users</b-button>
                    </div>
                </div>
            </div>

            <!-- Pending Invite -->
            <div class="col-lg-4 col-md-6 col-12 my-2">
                <div class="flex flex-col items-center">
                    <div class="flex items-center">
                        <div class="font-semibold text-uppercase">Pending Invite</div>
                        <div class="pl-3">
                            <i class="fa fa-info-circle text-grey"
                               data-toggle="tooltip"
                               data-placement="top"
                               title="It can take up to 24 hours after a user accepts their invite for their pending
                                      status to clear. They might not have access to some actions during this time.">
                            </i>
                        </div>
                    </div>
                    <div v-if="userStatsLoader" class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                    <div v-else class="font-size-21 font-weight-light">{{ userStats.pendingInvites }}</div>
                    <div class="no-btn-properties">
                        <b-button v-if="userStats.pendingInvites > 0" class="no-btn-properties" @click="resendInvites('all')">Resend invites</b-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Users List Filters -->
        <div class="row d-flex justify-content-between align-items-center my-3">
            <!-- Filters -->
            <div class="d-flex align-items-center">
                <!-- Status Filter -->
                <div :class="{'show-filter-selected': filterSelected.status.length > 0}">
                    <div class="dropdown">
                        <a  class="no-link-properties dropdown-toggle"
                            id="statusPopoverButton"
                            tabindex="0">
                            <span>{{ filterStatusLabel }}</span>
                        </a>
                        <span v-if="filterSelected.status.length > 0"
                              class="ml-2 text-grey pointer remove-filter-span"
                              @click.prevent="filterSelected.status = []">
                            &#x2715;
                        </span>
                        <!-- Dropdown Options -->
                        <b-popover target="statusPopoverButton" placement="bottom" custom-class="filter-popover-class" triggers="focus">
                            <div class="justify-content-center my-2">
                                <div v-for="(item, index) in filterOptions.status" :key="`checkbox-status-group-${index}`"
                                     class="px-3 pt-2 pb-1 hover:theme-custom-background hover:theme-custom-color">
                                    <label class="custom-checkbox">
                                        <input type="checkbox" v-model="filterSelected.status" :value="item.value" />
                                        <span class="checkmark"></span>
                                        {{ item.text }}
                                    </label>
                                </div>
                            </div>
                        </b-popover>
                    </div>
                </div>

                <!-- Last Active Filter -->
                <div class="ml-4" :class="{'show-filter-selected': filterSelected.lastActive}">
                    <div class="dropdown">
                        <a  class="no-link-properties dropdown-toggle"
                            id="lastActivePopoverButton"
                            tabindex="0">
                            <span>{{ filterLastActiveLabel }}</span>
                        </a>
                        <span v-if="filterSelected.lastActive"
                              class="ml-2 text-grey pointer remove-filter-span"
                              @click.prevent="changeLastActiveFilter(null)">
                            &#x2715;
                        </span>
                        <!-- Dropdown Options -->
                        <b-popover target="lastActivePopoverButton" placement="bottom" ref="LastActivePopover" custom-class="filter-popover-class" triggers="focus">
                            <div class="justify-content-center my-2">
                                <div v-for="(item, index) in filterOptions.lastActive" :key="`last-active-group-${index}`"
                                      @click.prevent="changeLastActiveFilter(item)"
                                      class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                    {{ item.text }}
                                </div>
                            </div>
                        </b-popover>
                    </div>
                </div>

                <!-- Teams Filter -->
                <div class="ml-4" :class="{'show-filter-selected': filterSelected.teams.length > 0}">
                    <div class="dropdown">
                        <a  class="no-link-properties dropdown-toggle"
                            id="teamPopoverButton"
                            tabindex="0">
                            <span>{{ filterTeamLabel }}</span>
                        </a>
                        <span v-if="filterSelected.teams.length > 0"
                              class="ml-2 text-grey pointer remove-filter-span"
                              @click.prevent="filterSelected.teams = []">
                            &#x2715;
                        </span>
                        <!-- Dropdown Options -->
                        <b-popover target="teamPopoverButton" placement="bottom" custom-class="filter-popover-class" triggers="focus">
                            <div class="justify-content-center my-2">
                                <div v-for="(item, index) in filterOptions.teams" :key="`checkbox-status-group-${index}`"
                                     class="px-3 pt-2 pb-1 hover:theme-custom-background hover:theme-custom-color">
                                    <label class="custom-checkbox">
                                        <input type="checkbox" v-model="filterSelected.teams" :value="item.id" />
                                        <span class="checkmark"></span>
                                        {{ item.name }}
                                    </label>
                                </div>
                            </div>
                        </b-popover>
                    </div>
                </div>

                <div class="ml-2">
                    <i class="fa fa-info-circle text-grey"
                       data-toggle="tooltip"
                       data-placement="top"
                       title="Select only one team to enable actions">
                    </i>
                </div>

                <!-- Clear Filters -->
                <div class="ml-4" v-if="showClearFilter">
                    <button class="btn btn-link" @click="clearFilters">Clear all</button>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex align-items-center">
                <!-- Action Dropdown -->
                <div class="dropdown border-1 border-primary py-2 px-3 border-radius-3">
                    <a  class="no-link-properties dropdown-toggle"
                        id="actionPopoverButton"
                        tabindex="0">
                        <span>Actions</span>
                    </a>
                    <b-popover
                        target="actionPopoverButton"
                        ref="ActionPopoverButton"
                        placement="bottom"
                        custom-class="filter-popover-class"
                        triggers="focus"
                        no-arrow>
                        <div class="justify-content-center nav-dropdown-menu my-2">
                            <div
                                @click="downloadLoginReport"
                                class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                Login Report
                            </div>
                            <div
                                @click="redirectToAllUserQuotas"
                                class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16"
                                :class="!enableUserActions ? 'disabled' : ''">
                                All User Quotas
                            </div>
                        </div>
                    </b-popover>
                </div>

                <!-- Create User -->
                <button class="btn btn-primary p-2 ml-2" @click="openCreateUserModal">Create user</button>
            </div>
        </div>

        <!-- Search Bar and Column Manager -->
        <div class="row bg-grey-custom d-flex align-items-center border-1">
            <!-- Search Input -->
            <div class="min-w-25 search-container">
                <input type="text"
                       v-model="filterSelected.search"
                       class="form-control focus:border-followup-nav"
                       placeholder="Search name or email address"
                       @input="debounceFilter">
                <i class="fa fa-search text-primary"></i>
            </div>

            <!-- Selected Members Count -->
            <div v-if="selectedUserIds.length > 0"
                class="text-dark ml-2">
                {{ selectedUserIds.length }} selected
            </div>

            <!-- Selected Members Actions -->
            <div v-if="selectedUserIds.length > 0" class="d-flex align-items-center ml-4">
                <b-button
                    v-if="nonRegisteredUsers"
                    :disabled="!enableUserActions"
                    class="no-btn-properties cursor-pointer font-size-14"
                    @click="resendInvites('selectedIds')">
                    <i class="fa fa-paper-plane text-primary"></i>
                    Resend invites
                </b-button>
                <b-button
                    v-if="!allowReactivateUsers"
                    :disabled="!enableUserActions || !isDeactivateEnabled"
                    class="no-btn-properties font-size-14"
                    :class="!isDeactivateEnabled ? 'cursor-not-allowed' : 'cursor-pointer'"
                    @click="deactivateUsers()">
                    <i class="fa fa-ban text-primary"></i>
                    Deactivate
                </b-button>
                <b-button
                    v-if="allowReactivateUsers"
                    :disabled="!enableUserActions"
                    class="no-btn-properties cursor-pointer font-size-14"
                    @click="activateUsers()">
                    <i class="fa fa-check text-primary"></i>
                    Reactivate
                </b-button>
                <b-button
                    :disabled="!enableUserActions"
                    @click="removeTeamUsers"
                    class="no-btn-properties cursor-pointer font-size-14">
                    <i class="fa fa-trash text-primary"></i>
                    Remove
                </b-button>
            </div>

            <!-- Column Selection -->
            <div class="ml-auto">
                <button type="button"
                        class="btn bg-grey-light btn-sm"
                        @click="openColumnSelectionLatestModal">
                    <i class="fa fa-columns pr-1"></i>
                    Edit Columns
                </button>
            </div>
        </div>

        <!-- Users List Table -->
        <div class="row border-1 position-relative">
            <!-- Table Loader -->
            <div v-if="usersListLoader" class="loader-overlay">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>

            <!-- Table -->
            <div class="table-responsive" :class="{'opacity-02': usersListLoader}">
                <table class="table table-bordered" ref="MembersTable" id="TeamMembersTable">
                    <thead class="text-nowrap">
                        <tr>
                            <!-- Select All Checkbox -->
                            <th scope="col" class="text-center align-middle" :class="noOfFrozenColumns > 0 ? 'sticky-col' : ''">
                                <label class="custom-checkbox mb-0">
                                    <input
                                        type="checkbox"
                                        @change="manageUserIds"
                                        :disabled="!enableUserActions"/>
                                    <span class="checkmark"></span>
                                </label>
                            </th>

                            <!-- Other Dynamic Columns -->
                            <th v-for="(column, index) in userListColumns" :key="index"
                                scope="col" class="text-uppercase font-size-13 align-middle cursor-pointer column-header-resize"
                                :class="[(index <= (noOfFrozenColumns - 1) ? 'sticky-col' : '')]">
                                <div class="column-resize" :class="`${column.property_name}`">
                                    <div @click="sortByColumn(column.property_name)" class="d-flex align-items-center justify-content-between mx-3">
                                        <div class="d-flex align-items-center">
                                            <div>{{ column.name }}</div>
                                            <div v-if="dateTimeColumnNames.includes(column.property_name)" class="ml-1">({{ userReadableTimezone ? userReadableTimezone : '' }})</div>
                                            <div v-if="tooltipColumnNames.includes(column.property_name)" class="ml-1">
                                                <i class="fa fa-info-circle text-grey-dark"
                                                   data-toggle="tooltip"
                                                   data-placement="top"
                                                   data-boundary="viewport"
                                                   :title="getColumnsTooltipContent(column.property_name)">
                                                </i>
                                            </div>
                                        </div>
                                        <div
                                            v-if="!['role', 'user_reports_to', 'teams'].includes(column.property_name)"
                                            class="d-flex flex-column ml-5">
                                            <i class="fa fa-sort-up h6 text-grey my-0 py-0 height-0"
                                               :class="currentOrder.sortBy === column.property_name && currentOrder.direction === 'asc' ? 'text-primary' : 'text-grey'">
                                            </i>
                                            <i class="fa fa-sort-down h6 text-grey my-0 py-0"
                                               :class="currentOrder.sortBy === column.property_name && currentOrder.direction === 'desc' ? 'text-primary' : 'text-grey'">
                                            </i>
                                        </div>
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(user, index) in users" :key="index"
                            class="bg-white hover:bg-grey-lighter table-row font-size-14 table-data-row">
                            <!-- Select Column Checkbox -->
                            <td class="align-middle text-center" :class="noOfFrozenColumns > 0 ? 'sticky-col' : ''">
                                <label class="custom-checkbox" v-if="user.default_role !== 'owner'">
                                    <input
                                        type="checkbox"
                                        v-model="selectedUserIds"
                                        :value="user.id"
                                        :disabled="!enableUserActions"/>
                                    <span class="checkmark"></span>
                                </label>
                            </td>

                            <!-- Other Dynamic Columns -->
                            <td v-for="(column, columnIndex) in userListColumns" :key="columnIndex"
                                :class="[(columnIndex <= (noOfFrozenColumns - 1) ? 'sticky-col' : '')]"
                                class="align-middle">
                                <!-- Name -->
                                <div v-if="column.property_name === 'name'" class="d-flex align-items-center position-relative name-cell">
                                    <div class="team-member-profile">
                                        <img :src="user.photo_url" class="spark-profile-photo">
                                    </div>
                                    <div class="d-flex flex-column align-items-baseline ml-2">
                                        <button class="btn btn-link p-0">{{ user.name }}</button>
                                        <div>{{ user.email }}</div>
                                    </div>

                                    <!-- Hover Buttons (Only within the Name Column) -->
                                    <div
                                        class="hover-buttons hover-buttons-div"
                                        :id="`hover-buttons-div-${index}`">
                                        <button
                                            v-if="user.default_role !== 'owner'"
                                            :disabled="!enableUserActions"
                                            class="btn bg-grey-light btn-sm"
                                            @click="editUserPermissions(user)">
                                            Edit permissions
                                        </button>
                                        <button
                                            class="btn bg-grey-light btn-sm dropdown-toggle"
                                            :id="`hoverActionPopoverButton-${index}`"
                                            :disabled="!enableUserActions"
                                            tabindex="0">
                                            Actions
                                        </button>
                                        <b-popover
                                            :target="`hoverActionPopoverButton-${index}`"
                                            :ref="`hoverActionPopoverButton-${index}`"
                                            placement="bottom"
                                            custom-class="filter-popover-class"
                                            triggers="focus"
                                            @shown="onPopoverOpen(index)"
                                            @hidden="onPopoverClose(index)">
                                            <div class="justify-content-center nav-dropdown-menu my-2">
                                                <div
                                                    @click="redirectToUserSettings(user.id)"
                                                    class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                                    User Settings
                                                </div>
                                                <div
                                                    @click="redirectToUserQuotas(user.id)"
                                                    class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                                    User Quotas
                                                </div>
                                                <div
                                                    @click="redirectToUserSalesBehaviorSettings(user.id)"
                                                    class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                                    User Sales Behavior Settings
                                                </div>
                                                <div
                                                    v-if="user.default_role !== 'owner'"
                                                    @click="editUserTeams(user)"
                                                    class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                                    Edit teams
                                                </div>
                                                <div
                                                    v-if="user.default_role !== 'owner'"
                                                    @click="copyPermissionsToNewUser(user.id)"
                                                    class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                                    Copy permissions to new users
                                                </div>
                                                <div
                                                    @click="editUserColor(user)"
                                                    class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                                    Edit Color
                                                </div>
                                                <div
                                                    v-if="user.default_role !== 'owner'"
                                                    @click="lockUnlockUser(user.id, user.is_locked)"
                                                    class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                                    {{ user.is_locked ? 'Unlock' : 'Lock' }}
                                                </div>
                                                <div
                                                    @click="resetPasswordLink(user)"
                                                    class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                                    Reset password
                                                </div>
                                                <div v-if="user.default_role !== 'owner'" class="dropdown-divider"></div>
                                                <div
                                                    v-if="user.default_role !== 'owner' && user.is_active"
                                                    @click="deactivateUsers(user.id)"
                                                    class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                                    Deactivate user
                                                </div>
                                                <div
                                                    v-if="user.default_role !== 'owner' && !user.is_active"
                                                    @click="activateUsers(user.id)"
                                                    class="py-2 px-3 pointer hover:theme-custom-background hover:theme-custom-color font-size-16">
                                                    Reactivate user
                                                </div>
                                                <div v-if="user.default_role !== 'owner'" class="py-2 px-3">
                                                    <small>Only deactivated users can be removed from your account.</small>
                                                </div>
                                            </div>
                                        </b-popover>
                                    </div>
                                </div>

                                <!-- Manager -->
                                <div v-else-if="column.property_name === 'user_reports_to'">
                                    <span> {{ user.user_manager ? user.user_manager : '--' }} </span>
                                </div>

                                <!-- Teams -->
                                <div v-else-if="column.property_name === 'teams'">
                                    <span> {{ user.user_teams_name ? user.user_teams_name : '--' }} </span>
                                </div>

                                <!-- Invite Status -->
                                <div v-else-if="column.property_name === 'invite_status'">
                                    <span v-if="user.is_registered" class="status-icon enabled-status-icon"></span>
                                    <span v-else class="status-icon pending-invite-status-icon"></span>
                                    <span> {{ user.is_registered ? 'Invite accepted' : 'Invite pending' }} </span>
                                </div>

                                <!-- Email Status -->
                                <div v-else-if="column.property_name === 'external_email'">
                                    <span class="status-icon" :class="[(user.email_status ? 'enabled-status-icon' : 'disabled-status-icon')]"></span>
                                    <span> {{ user.email_status ? 'Enabled' : 'Disabled' }} </span>
                                </div>

                                <!-- Calendar Status -->
                                <div v-else-if="column.property_name === 'selected_calendar_id'">
                                    <span class="status-icon" :class="[(user.is_calendar_connected ? 'enabled-status-icon' : 'disabled-status-icon')]"></span>
                                    <span> {{ user.is_calendar_connected ? 'Connected' : 'Disconnected' }} </span>
                                </div>

                                <!-- Created Date -->
                                <div v-else-if="column.property_name === 'created_at'">
                                    <div class="d-flex flex-column">
                                        <div>{{ formatTimestampDate(user.created_at) }} {{ formatTimestampTime(user.created_at) }}</div>
                                        <div>{{ user.created_by_user ? user.created_by_user : '--'}}</div>
                                    </div>
                                </div>

                                <!-- Last Password Change Date -->
                                <div v-else-if="column.property_name === 'last_password_changed_at'">
                                    <div v-if="user.last_password_changed_at" class="d-flex flex-column">
                                        <div>{{ formatTimestampDate(user.last_password_changed_at) }}</div>
                                        <small>{{ formatTimestampTime(user.last_password_changed_at) }}</small>
                                    </div>
                                    <span v-else>--</span>
                                </div>

                                <!-- Other Columns -->
                                <div v-else>
                                    {{ user[column.property_name] ? user[column.property_name] : '--' }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div class="d-flex m-auto align-items-center py-3">
                <div class="text-grey-darker">Total of {{ pagination.meta.total }} records</div>
                <b-pagination
                    v-if="pagination.meta.total > pagination.meta.per_page"
                    v-model="pagination.meta.current_page"
                    :total-rows="pagination.meta.total"
                    :per-page="pagination.meta.per_page"
                    class="ml-2"
                    first-text="First"
                    prev-text="Prev"
                    next-text="Next"
                    last-text="Last"
                    @change="goToPage">
                </b-pagination>
                <div class="ml-2" v-if="pagination.meta.total > 0">
                    <b-form-select
                        v-model="itemsPerPage"
                        :options="itemPerPageOptions"
                        class="text-teal border-0 bg-transparent"
                        value-field="value"
                        @change="changeItemPerPage">
                    </b-form-select>
                </div>
            </div>
        </div>

        <table-column-selection-latest
            ref="TableColumnSelectionLatest"
            :table-columns="userListColumns"
            :no-of-frozen-columns="noOfFrozenColumns"
            :user-list-default-columns="userListDefaultColumns"
            v-on:saveColumnState="saveUserListColumns">
        </table-column-selection-latest>

        <manage-user-permissions-modal
            v-if="enableUserActions"
            :team="selectedPermissionTeam"
            parent-id="Users"
            ref="ManageUserPermissionsModal"
            v-on:user-permissions-updated="userPermissionsUpdated">
        </manage-user-permissions-modal>

        <create-user-modal
            :team="team"
            ref="CreateUserModal"
            v-on:users-created="usersCreated">
        </create-user-modal>

        <edit-user-color-modal
            parent-id="Users"
            ref="EditUserColorModal">
        </edit-user-color-modal>

        <edit-user-teams-modal
            parent-id="Users"
            ref="EditUserTeamsModal"
            v-on:user-teams-updated="getUsers">
        </edit-user-teams-modal>
    </b-container>
</template>

<script>

import TableColumnSelectionLatest from "../../../../shared/table-column-selection-latest.vue";
import ManageUserPermissionsModal from "./manage-user-permissions-modal.vue";
import CreateUserModal from "./create-user-modal.vue";
import EditUserColorModal from "./edit-user-color-modal.vue";
import EditUserTeamsModal from "./edit-user-teams-modal.vue";

export default {
    name: 'users-management-tab',
    components: {
        EditUserTeamsModal,
        EditUserColorModal, CreateUserModal, ManageUserPermissionsModal, TableColumnSelectionLatest},
    props: ['user', 'team'],
    data() {
        return {
            userStats: {
                totalUsers: 0,
                deactivatedUsers: 0,
                pendingInvites: 0
            },
            userListColumns: [],
            users: [],
            pagination: {
                meta: {},
                links: {}
            },
            filterOptions: {
                status: [
                    { text: 'Invite accepted', value: 'invite-accepted' },
                    { text: 'Invite pending', value: 'invite-pending' },
                    { text: 'Deactivated', value: 'deactivated' },
                ],
                lastActive: [
                    { text: 'Last 7 days', value: 'last-7-days' },
                    { text: 'Last 14 days', value: 'last-14-days' },
                    { text: 'Last 30 days', value: 'last-30-days' },
                    { text: 'Last 90 days', value: 'last-90-days' },
                    { text: 'Last 180 days', value: 'last-180-days' },
                    { text: 'Last 365 days', value: 'last-365-days' },
                ],
                teams: []
            },
            filterSelected: {
                teams: [],
                status: [],
                search: null,
                timeout: null,
                lastActive: null
            },
            currentOrder: {
                sortBy: 'name',
                direction: 'asc'
            },
            userReadableTimezone: null,
            usersListLoader: false,
            tooltipColumnNames: ['external_email', 'selected_calendar_id', 'calendar_sync_status'],
            dateTimeColumnNames: ['last_login', 'last_password_changed_at', 'created_at'],
            teamMemberWidth: null,
            selectedUserIds: [],
            allUserStatus: [],
            allUserIds: [],
            userListDefaultColumns: [],
            noOfFrozenColumns: 0,
            userStatsLoader: false,
            itemsPerPage: 15,
            itemPerPageOptions: [
                { value: 15, text: '15 per page' },
                { value: 25, text: '25 per page' },
                { value: 50, text: '50 per page' }
            ]
        }
    },
    async mounted() {
        this.getUserReadableTimezone();
        this.getUserStats();
        this.getUserAllTeams();
        await this.getUserListColumns();
        this.getWidth();
        window.addEventListener("load", this.adjustStickyColumns);
        window.addEventListener("resize", this.adjustStickyColumns);

        let self = this;
        Bus.$on('userUpdatedMultipleTeamsTab', function(data) {
            self.getUsers();
        })
    },
    watch: {
        'filterSelected.status': {
            handler: function(val, oldVal) {
                this.resetSortingOrder();
                this.$nextTick(() => {
                    this.getUsers();
                });
            }
        },
        'filterSelected.teams': {
            handler: function(val, oldVal) {
                this.resetSortingOrder();
                this.$nextTick(() => {
                    this.getUserStats();
                    this.getUsers();
                });
            }
        }
    },
    computed: {
        filterStatusLabel() {
            if(this.filterSelected.status.length > 0) {
                return 'Status (' + this.filterSelected.status.length + ')';
            }
            return 'Status';
        },
        filterLastActiveLabel() {
            if(this.filterSelected.lastActive) {
                const item = this.filterOptions.lastActive.find(obj => obj.value === this.filterSelected.lastActive);
                return item ? item.text : '';
            }
            return 'Last Active';
        },
        filterTeamLabel() {
            if(this.filterSelected.teams.length > 0) {
                return 'Team (' + this.filterSelected.teams.length + ')';
            }
            return 'Team';
        },
        showClearFilter() {
            return this.filterSelected.status.length !== 0 ||
                this.filterSelected.lastActive !== null ||
                this.filterSelected.teams.length !== 0;
        },
        isDeactivateEnabled() {
            let self = this;

            return _.every(self.selectedUserIds, id =>
                id !== self.user.id && _.some(self.allUserStatus, { id, active: 1 })
            );
        },
        allowReactivateUsers() {
            let self = this;

            return _.every(self.selectedUserIds, id =>
                _.some(self.allUserStatus, { id, active: 0 })
            );
        },
        nonRegisteredUsers() {
            let self = this;

            return _.every(self.selectedUserIds, id =>
                _.some(self.users, { id, is_registered: false })
            );
        },
        selectedPermissionTeam() {
            return this.filterOptions.teams.find(team => team.id === this.getActionTeamId)
        },
        enableUserActions() {
            return this.filterSelected.teams.length === 1 || this.filterOptions.teams.length === 1;
        },
        getActionTeamId() {
            return this.filterOptions.teams.length === 1 ? this.filterOptions.teams[0].id : this.filterSelected?.teams?.[0];
        }
    },
    beforeDestroy() {
        window.removeEventListener("load", this.adjustStickyColumns);
        window.removeEventListener("resize", this.adjustStickyColumns);
    },
    methods: {
        getUserReadableTimezone() {
            const options = { timeZoneName: 'short' };
            const formatter = new Intl.DateTimeFormat('en-US', options);
            const parts = formatter.formatToParts(new Date());

            this.userReadableTimezone = parts.find(part => part.type === 'timeZoneName').value;
        },
        getUserStats() {
            let self = this;
            self.userStatsLoader = true;
            const payload = {
                filters: JSON.stringify(self.filterSelected)
            };

            axios.get('/settings/'+this.team.id+'/members/stats', {params: payload}).then(({data}) => {
                self.userStatsLoader = false;
                self.userStats = data;
            }).catch((error) => {
                self.userStatsLoader = false;
            });
        },
        goToPage(page) {
            this.getUsers(page);
        },
        async getUserListColumns() {
            let self = this;
            self.usersListLoader = true;

            await axios.get('/settings/'+this.team.id+'/members/columns').then(({data}) => {
                self.userListColumns = data.columns;
                self.noOfFrozenColumns = data.noOfFrozenColumns;
                self.userListDefaultColumns = data.defaultAvailableColumns;
                self.itemsPerPage = data.itemsPerPage;
                self.getUsers();
            }).catch((error) => {
                self.usersListLoader = false;
            });
        },
        async saveUserListColumns(columns) {
            let self = this;
            self.usersListLoader = true;

            await axios.post('/settings/'+this.team.id+'/members/columns', {columns}).then(async ({data}) => {
                await self.getUserListColumns();
                self.getWidth();
            }).catch((error) => {
                self.usersListLoader = false;
            });
        },
        getUsers(page = 1) {
            let self = this;
            self.usersListLoader = true;
            self.selectedUserIds = [];

            const payload = {
                page,
                filters: JSON.stringify(self.filterSelected),
                currentOrder: JSON.stringify(self.currentOrder),
                itemsPerPage: self.itemsPerPage
            };

            axios.get('/settings/'+this.team.id+'/members/all', {params: payload}).then(({data}) => {
                self.usersListLoader = false;
                self.users = data.data;
                self.pagination.links = data.links;
                self.pagination.meta = data.meta;
                self.allUserStatus = data.allUserStatus;
                self.allUserIds = _.map(data.allUserStatus, 'id');
                self.manageTooltip();
            }).catch((error) => {
                self.usersListLoader = false;
            });
        },
        manageTooltip() {
            const tooltipSelector = $('[data-toggle="tooltip"]');

            tooltipSelector.tooltip({
                trigger: 'hover'
            });
        },
        clearFilters() {
            this.filterSelected.status = [];
            this.filterSelected.lastActive = null;
            this.filterSelected.teams = [];
            this.getUsers();
        },
        formatTimestampDate(timestamp) {
            return moment.utc(timestamp).local().format("MMMM D, YYYY");
        },
        formatTimestampTime(timestamp) {
            return moment.utc(timestamp).local().format("h:mm A");
        },
        debounceFilter() {
            clearTimeout(this.filterSelected.timeout);
            this.filterSelected.timeout = setTimeout(() => {
                this.getUsers();
            }, 700, this);
        },
        changeLastActiveFilter(item = null) {
            this.filterSelected.lastActive = item ? item.value : null;
            this.$refs.LastActivePopover.$emit('close');
            this.getUsers();
        },
        sortByColumn(sortBy) {
            if(!_.includes(['role', 'user_reports_to', 'teams'], sortBy)) {
                if(sortBy === this.currentOrder.sortBy) {
                    this.currentOrder.direction = (this.currentOrder.direction === 'asc') ? 'desc' : 'asc'
                }
                else {
                    this.currentOrder.sortBy = sortBy
                    this.currentOrder.direction = 'desc';
                }
                this.getUsers();
            }
        },
        getColumnsTooltipContent(columnName) {
            if(columnName === 'external_email') {
                return 'Personal email accounts connected to FollowupCRM to log, track, send, and receive emails.'
            }
            else if(columnName === 'selected_calendar_id') {
                return 'Personal calendar connected to use FollowupCRM Meetings, calendar sync and scheduling pages.'
            }
            else if(columnName === 'calendar_sync_status') {
                return 'Calendar sync logs new meetings from their primary calendar to existing contacts in FollowupCRM.'
            }
        },
        getWidth() {
            let self = this;
            self.fetchWidth();
            $(window).mouseup((event) => {
                let widthCls = $(event.target).attr('class');
                let words = '';
                let cls1 = '';
                if(widthCls.includes('column-resize')) {
                    words = widthCls.split(' ');
                    cls1 = words[1];
                    if(self.teamMemberWidth && Object.keys(self.teamMemberWidth).length > 0) {
                        self.teamMemberWidth[cls1] = $('.' + cls1).width();
                        self.saveWidth(self.teamMemberWidth);
                    }
                    else {
                        let columnWidth = {};
                        columnWidth[cls1] = $('.' + cls1).width();
                        self.saveWidth(columnWidth);
                    }
                }
            });
        },
        fetchWidth() {
            let self = this;

            axios.get('/settings/'+this.team.id+'/members/columns/width').then(({data}) =>{
                self.teamMemberWidth = data.width;
                if(self.teamMemberWidth && Object.keys(self.teamMemberWidth).length > 0) {
                    for(let i = 0; i < Object.keys(self.teamMemberWidth).length; i++) {
                        $('.' + Object.keys(self.teamMemberWidth)[i].toString()).width(Object.values(self.teamMemberWidth)[i].toString() + 'px');
                    }
                }
                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        this.adjustStickyColumns();
                    });
                });
            });
        },
        saveWidth(width) {
            let self = this;

            axios.post('/settings/'+this.team.id+'/members/columns/width', {width}).then(data => {
                self.fetchWidth();
            });
        },
        reviewDeactivatedUsers() {
            if(!this.filterSelected.status.includes('deactivated')) {
                this.filterSelected.status.push('deactivated');
            }
        },
        downloadLoginReport() {
            this.$refs.ActionPopoverButton.$emit('close');

            axios.get('/settings/teams/'+this.team.id+'/login-report', { responseType: 'blob' }).then((response) => {
                // Get the Content-Disposition header
                const contentDisposition = response.headers['content-disposition'];

                // Extract the filename using a regex
                let fileName = 'login-report.xlsx';
                const fileNameMatch = contentDisposition && contentDisposition.match(/filename\*?=(['"]?)(.+?)\1/);
                if(fileNameMatch && fileNameMatch[2]) {
                    fileName = decodeURIComponent(fileNameMatch[2].replace(/['"]/g, ''));
                }

                // Create a Blob and trigger the download
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }).catch((error) => {
                console.error('Download failed:', error);
            });
        },
        manageUserIds() {
            if(this.selectedUserIds.length === this.allUserIds.length) {
                this.selectedUserIds = [];
            }
            else {
                this.selectedUserIds = _.cloneDeep(this.allUserIds);
            }
        },
        onPopoverOpen(index) {
            $(`#hover-buttons-div-${index}`).removeClass("hover-buttons");
        },
        onPopoverClose(index) {
            $(`#hover-buttons-div-${index}`).addClass("hover-buttons");
        },
        redirectToUserSettings(userId) {
            if(this.getActionTeamId !== null && this.getActionTeamId !== undefined) {
                window.location = route('settings.member.notification-settings.edit-user-settings', {team: this.getActionTeamId, member: userId});
            }
        },
        openColumnSelectionLatestModal() {
            this.$refs.TableColumnSelectionLatest.open();
        },
        adjustStickyColumns() {
            this.$nextTick(() => {
                let leftOffset = 0;

                for(let i = 0; i <= this.noOfFrozenColumns; i++) {
                    let columnCells = this.$refs.MembersTable.querySelectorAll(`th:nth-child(${i+1}), td:nth-child(${i+1})`);
                    let columnWidth = columnCells[0] ? columnCells[0].offsetWidth : 0;

                    columnCells.forEach(cell => {
                        cell.style.left = leftOffset + "px";
                    });

                    leftOffset += columnWidth;
                }
            });
        },
        editUserPermissions(user) {
            this.$refs.ManageUserPermissionsModal.open(user);
        },
        openCreateUserModal() {
            this.$refs.CreateUserModal.open();
        },
        resendInvites(type) {
            let self = this;
            const payload = {
                type,
                selectedUserIds: self.selectedUserIds,
                selectedTeamIds: self.filterOptions.teams.length === 1 ? [self.filterOptions.teams[0].id] : (self.filterSelected.teams.length === 0 ? _.map(self.filterOptions.teams, 'id') : self.filterSelected.teams)
            };

            Swal.fire({
                title: 'Warning',
                text: 'Are you sure you want to resend invites to user(s)?',
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#01B1AF',
                confirmButtonText: 'Proceed'
            }).then((result) => {
                if (result.value) {
                    axios.post('/settings/members/resend-invites', payload).then(data => {
                        Swal.fire({
                            title: 'Invites Sent',
                            text: 'The invites have resent to user(s)!',
                            type: 'success'
                        });
                        self.getUsers();
                    });
                }
            });
        },
        usersCreated() {
            this.getUserStats();
            this.getUsers();
        },
        userPermissionsUpdated() {
            this.getUsers();
            this.refreshCreateUserData();
        },
        getUserAllTeams() {
            let self = this;

            axios.get('/settings/'+this.team.id+'/members/get-all-teams').then(({data}) => {
                self.filterOptions.teams = data.data;
            }).catch((error) => {
                console.error(error);
            });
        },
        redirectToUserQuotas(userId) {
            if(this.getActionTeamId !== null && this.getActionTeamId !== undefined) {
                window.location = route('settings.member.quota.edit-user-quota', {team: this.getActionTeamId, member: userId});
            }
        },
        redirectToUserSalesBehaviorSettings(userId) {
            if(this.getActionTeamId !== null && this.getActionTeamId !== undefined) {
                window.location = route('settings.teams.member.sales-behavior-user-settings.edit-user-sales-behavior', {team: this.getActionTeamId, member: userId});
            }
        },
        resetPasswordLink(user) {
            Swal.fire({
                title: 'Warning',
                text: 'Are you sure you want to send reset password link to ' + user.name + '!',
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#01B1AF',
                confirmButtonText: 'Send Link'
            }).then((result) => {
                if (result.value) {
                    axios.post('/settings/members/send/reset-password/email', {email: user.email}).then((response) => {
                        this.getToast().fire({
                            type: 'success',
                            title: 'Reset password email sent successfully!'
                        });
                    }).catch((error) => {
                        if(error.response && error.response.status === 422) {
                            this.getToast().fire({
                                type: 'error',
                                title: error.response.data.errors.email[0]
                            });
                        }
                    });
                }
            });
        },
        redirectToAllUserQuotas() {
            window.location = route('settings.teams.member.all.quota.edit', {team: this.getActionTeamId});
        },
        lockUnlockUser(userId, isLocked) {
            Swal.fire({
                title: 'Warning',
                text: 'Are you sure you want to ' + (isLocked ? 'unlock' : 'lock') + ' this user!',
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#01B1AF',
                confirmButtonText: 'Proceed'
            }).then((result) => {
                if(result.value) {
                    let url = isLocked ? '/settings/teams/'+this.team.id+'/members/'+userId+'/unlock' : '/settings/teams/'+this.team.id+'/members/'+userId+'/lock';
                    axios.post(url).then((response) => {
                        this.getToast().fire({
                            type: 'success',
                            title: response.data.message
                        });
                    });
                    this.getUsers();
                }
            });
        },
        activateUsers(userId = null) {
            if(this.getActionTeamId !== null && this.getActionTeamId !== undefined) {
                const userIds = userId ? [userId] : this.selectedUserIds;

                Swal.fire({
                    title: 'Warning',
                    text: 'Are you sure you want to activate selected person(s)?',
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#01B1AF',
                    confirmButtonText: 'Proceed'
                }).then((result) => {
                    if(result.value) {
                        axios.post('/settings/'+this.getActionTeamId+'/members/update/activate-multiple-members', {userIds}).then((response) => {
                            this.getToast().fire({
                                type: 'success',
                                title: 'Users reactivated successfully!'
                            });
                        });
                        this.getUsers();
                    }
                });
            }
        },
        deactivateUsers(userId = null) {
            if(this.getActionTeamId !== null && this.getActionTeamId !== undefined) {
                const userIds = userId ? [userId] : this.selectedUserIds;

                Swal.fire({
                    title: 'Warning',
                    text: 'Are you sure you want to deactivate selected person(s)?',
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#01B1AF',
                    confirmButtonText: 'Proceed'
                }).then((result) => {
                    if(result.value) {
                        axios.post('/settings/'+this.getActionTeamId+'/members/update/deactivate-multiple-members', {userIds}).then((response) => {
                            this.getToast().fire({
                                type: 'success',
                                title: 'Users deactivated successfully!'
                            });
                        });
                        this.getUsers();
                    }
                });
            }
        },
        refreshCreateUserData() {
            this.$refs.CreateUserModal.$refs.ChooseSetAccess.getTeamAllUsers();
        },
        copyPermissionsToNewUser(userId) {
            this.$refs.CreateUserModal.$refs.ChooseSetAccess.copyOldUserPermission(userId, this.getActionTeamId);
            this.$refs.CreateUserModal.open();
        },
        resetSortingOrder() {
            this.currentOrder.sortBy = 'name';
            this.currentOrder.direction = 'asc';
        },
        editUserColor(user) {
            this.$refs.EditUserColorModal.open(user, this.getActionTeamId);
        },
        editUserTeams(user) {
            this.$refs.EditUserTeamsModal.open(user, this.getActionTeamId, this.filterOptions.teams);
        },
        removeTeamUsers() {
            if(this.getActionTeamId !== null && this.getActionTeamId !== undefined) {
                const userIds = this.selectedUserIds;

                Swal.fire({
                    title: 'Warning',
                    html: '<p class="text-danger">This will permanently delete the '+ (userIds.length > 1 ? "users" : "user") +' and all projects, contacts and companies associated with '+ (userIds.length > 1 ? "them" : "it") +'.</p>',
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#01B1AF',
                    confirmButtonText: 'Proceed'
                }).then((result) => {
                    if(result.value) {
                        axios.post('/settings/'+this.getActionTeamId+'/members/remove/user-from-teams', {userIds}).then((response) => {
                            this.getToast().fire({
                                type: 'success',
                                title: 'User(s) removed from team successfully!'
                            });
                        });
                        this.getUsers();
                    }
                });
            }
        },
        changeItemPerPage() {
            this.$nextTick(() => {
                this.getUsers();
                this.updateItemsPerPage();
            });
        },
        updateItemsPerPage() {
            const perPage = this.itemsPerPage;
            axios.post('/settings/members/listing/update/items-per-page', {perPage});
        }
    }
}
</script>

<style scoped>
.no-btn-properties {
    background: none;
    color: #01B1AF;
    border: none;
    box-shadow: none;
}
.font-size-13 {
    font-size: 13px;
}
.font-size-14 {
    font-size: 14px;
}
.font-size-16 {
    font-size: 16px;
}
.font-size-21 {
    font-size: 21px;
}
.font-weight-600 {
    font-weight: 600;
}
#TeamMembersTable thead th {
    background-color: #f5f8fa !important;
}
.bg-grey-custom {
    background-color: #f5f8fa !important;
}
.height-0 {
    height: 0px;
}
.team-member-hover-action-buttons {
    visibility: hidden;
    opacity: 0;
    transition: visibility 0s, opacity 0.2s linear;
    padding: 5px 5px 5px 25px;
    background-color: white;
    margin-left: -10%;
}
#TeamMembersTable tbody tr:hover .team-member-hover-action-buttons {
    visibility: visible;
    opacity: 1;
}
.no-link-properties {
    text-decoration: none;
    cursor: pointer;
}
.show-filter-selected {
    background: #c7eeee7d;
    padding: 8px 15px;
    border-radius: 3px;
}
.min-w-25 {
    min-width: 25%;
}
.border-radius-3 {
    border-radius: 3px;
}
.custom-checkbox {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 16px;
    user-select: none;
    gap: 8px;
}
.custom-checkbox input[type="checkbox"] {
    display: none;
}
.custom-checkbox .checkmark {
    width: 24px;
    height: 24px;
    background-color: #fff;
    border: 2px solid #01B1AF;
    border-radius: 4px;
    display: inline-block;
    position: relative;
    transition: background-color 0.3s, border-color 0.3s;
}
.custom-checkbox input[type="checkbox"]:checked + .checkmark {
    background-color: #01B1AF;
    border-color: #01B1AF;
}
.custom-checkbox input[type="checkbox"]:checked + .checkmark::after {
    content: '';
    position: absolute;
    top: 45%;
    left: 50%;
    width: 8px;
    height: 15px;
    border: solid #fff;
    border-width: 0 3px 3px 0;
    transform: translate(-50%, -50%) rotate(45deg);
}
.search-container {
    position: relative;
}
.search-container i {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}
.team-member-profile img {
    max-width: unset;
}
.status-icon {
    height: 10px;
    width: 10px;
    border-radius: 100%;
    display: inline-block;
    margin-right: 8px;
    aspect-ratio: 1 / 1;
}
.disabled-status-icon {
    background-color: #cbd6e2;
}
.enabled-status-icon {
    background-color: #00bda5;
}
.pending-invite-status-icon {
    background-color: rgb(245, 194, 107);
}
.opacity-02 {
    opacity: 0.2;
}
.loader-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.7);
    width: 100%;
    height: 100%;
}
.filter-popover-class {
    .popover-body {
        height: auto !important;
        padding: 0 !important;
    }
}
.hover\:theme-custom-background:hover {
    background-color: #01b1af2e;
}
.hover\:theme-custom-color:hover {
    color: #49545a;
}
#TeamMembersTable th.column-header-resize {
    padding: 0;
    margin: 0px;
}
#TeamMembersTable th.column-header-resize div.column-resize {
    resize: horizontal;
    overflow: auto;
    min-width: 90%;
    margin: 0px;
    padding: 0px;
    display: block;
    border: 0;
}
.btn-secondary.disabled, .btn-secondary:disabled {
    opacity: 0.5 !important;
}
.hover-buttons {
    visibility: hidden;
    opacity: 0;
    transition: visibility 0.2s, opacity 0.2s ease-in-out;
}
.hover-buttons,
.hover-buttons-div {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    z-index: 2;
    white-space: nowrap;
}
.table-data-row:hover .hover-buttons {
    visibility: visible;
    opacity: 1;
}
.table-data-row:hover .team-member-profile,
.table-data-row:hover .d-flex.flex-column {
    opacity: 0.3;
}
@media (max-width: 768px) {
    .hover-buttons,
    .hover-buttons-div {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
}
th.sticky-col, td.sticky-col {
    position: sticky;
    background: #fff;
    z-index: 2;
    border: 1px solid #c7cdd1;
}
th.sticky-col,
td.sticky-col {
    z-index: 3;
}
</style>
